import type { DetectionProfile } from '../domain/profile'
import type { EngineEvent, NotAdmissibleReason, SuspendReason } from '../domain/types'

// Libellés français des décisions du moteur. En mode observation, les décisions sont formulées au conditionnel
// (« aurait capturé », « aurait terminé ») : rien n'est appliqué.

export interface EventText {
  decision: string
  motive: string
  tone: 'info' | 'success' | 'warning' | 'danger'
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`
}

const suspendMotives: Record<SuspendReason, (profile: DetectionProfile) => string> = {
  INACCURATE: (profile) => `Précision au-delà de ${String(profile.maxAccuracyM)} m`,
  STALE: (profile) => `Relevé plus vieux que ${String(profile.maxFixAgeS)} s`,
  JUMP: () => 'Saut de position incohérent',
  SIGNAL_LOST: () => 'Signal perdu',
  CLOCK_ANOMALY: () => 'Recul de l’horloge de la tablette',
}

function notAdmissibleMotive(reason: NotAdmissibleReason, profile: DetectionProfile, observedMs: number, movingMs: number) {
  if (reason === 'TOO_SHORT') {
    return `trop court (${formatDuration(observedMs)} < ${String(profile.minInterventionS)} s)`
  }
  const share = observedMs === 0 ? 0 : Math.round((movingMs / observedMs) * 100)
  return `immobile : ${String(share)} % du temps en mouvement (< ${String(Math.round(profile.minMovingShare * 100))} %)`
}

export function describeEvent(
  event: EngineEvent,
  labelOf: (itemId: string) => string,
  profile: DetectionProfile,
  observing: boolean,
): EventText {
  switch (event.kind) {
    case 'CANDIDATE':
      return {
        decision: `Candidate ${labelOf(event.itemId)}`,
        motive: `À ${String(event.distanceM)} m du drapeau`,
        tone: 'info',
      }
    case 'CANDIDATE_LOST':
      return {
        decision: `Candidate abandonnée ${labelOf(event.itemId)}`,
        motive: event.observationIds.length > 0 ? 'Sortie du rayon de capture' : 'Séquence interrompue',
        tone: 'info',
      }
    case 'CAPTURED':
      return {
        decision: observing ? `Aurait capturé ${labelOf(event.itemId)}` : `Capture ${labelOf(event.itemId)}`,
        motive:
          event.source === 'AUTO'
            ? `${String(event.observationIds.length)} positions cohérentes`
            : 'Adresse choisie par l’opérateur; le choix confirme l’identité, pas le travail',
        tone: 'success',
      }
    case 'AMBIGUITY':
      return {
        decision: `Ambiguïté ${event.itemIds.map(labelOf).join(' / ')}`,
        motive: 'Entrées indiscernables : choix demandé, tâche active conservée',
        tone: 'warning',
      }
    case 'AMBIGUITY_CLEARED':
      return {
        decision: 'Fin de l’ambiguïté',
        motive:
          event.reason === 'SITE_CHOSEN'
            ? 'Adresse choisie'
            : event.reason === 'NONE_CHOSEN'
              ? 'Aucune : je passe seulement'
              : 'Véhicule éloigné de toutes les adresses en cause',
        tone: 'info',
      }
    case 'COMPLETED':
      if (event.source === 'MANUAL') {
        return {
          decision: `${labelOf(event.itemId)} terminée à la main`,
          motive: event.reason,
          tone: 'success',
        }
      }
      return {
        decision: observing ? `Aurait terminé ${labelOf(event.itemId)}` : `${labelOf(event.itemId)} terminée`,
        motive: `${formatDuration(event.observedMs)} compté ≥ ${String(profile.minInterventionS)} s · ${
          event.trigger === 'DEPARTURE' ? 'départ confirmé' : 'entrée suivante confirmée'
        }`,
        tone: 'success',
      }
    case 'VISIT_NOT_ADMISSIBLE':
      return {
        decision: observing
          ? `Aurait remis à faire ${labelOf(event.itemId)}`
          : `${labelOf(event.itemId)} revient à faire`,
        motive: event.reasons
          .map((reason) => notAdmissibleMotive(reason, profile, event.observedMs, event.movingMs))
          .join(' · '),
        tone: 'warning',
      }
    case 'PROBLEM_REPORTED':
      return {
        decision: `Problème ${labelOf(event.itemId)}`,
        motive: 'Signalé par l’opérateur; le GPS ne l’efface pas',
        tone: 'danger',
      }
    case 'REOPENED':
      return { decision: `${labelOf(event.itemId)} rouverte`, motive: event.reason, tone: 'warning' }
    case 'DETECTION_SUSPENDED':
      return {
        decision: 'Détection suspendue',
        motive: `${suspendMotives[event.reason](profile)}; temps gelé, aucune extrapolation`,
        tone: 'warning',
      }
    case 'DETECTION_RESUMED':
      return {
        decision: 'Reprise',
        motive: `${String(Math.round(event.suspendedMs / 1000))} s non créditées`,
        tone: 'info',
      }
    case 'OBSERVATION_IGNORED':
      return { decision: 'Position ignorée', motive: 'Déjà reçue ou hors ordre', tone: 'info' }
    case 'PAUSED':
      return { decision: 'Pause', motive: 'Aucun temps compté', tone: 'info' }
    case 'RESUMED':
      return { decision: 'Fin de la pause', motive: 'Reprise de la détection', tone: 'info' }
    case 'ACTION_REFUSED':
      return { decision: 'Action refusée', motive: event.reason, tone: 'danger' }
  }
}
